<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/AppIcon.vue'
import StatusBadge from '@/components/StatusBadge.vue'
import { useAdminStore } from '@/stores/admin'
import type { AppIconName } from '@/types/icons'

interface NavigationItem {
  readonly to: string
  readonly label: string
  readonly icon: AppIconName
}

const route = useRoute()
const router = useRouter()
const store = useAdminStore()
const { currentUser, health, settings } = storeToRefs(store)
const menuOpen = ref(false)

const navigation: readonly NavigationItem[] = [
  { to: '/admin', label: 'Ringkasan', icon: 'home' },
  { to: '/admin/warung', label: 'Warung', icon: 'store' },
  { to: '/admin/akun', label: 'Akun Pengguna', icon: 'users' },
  { to: '/admin/perangkat', label: 'Perangkat', icon: 'device' },
  { to: '/admin/sinkronisasi', label: 'Sinkronisasi', icon: 'sync' },
  { to: '/admin/aktivitas', label: 'Aktivitas', icon: 'activity' },
  { to: '/admin/rilis-aplikasi', label: 'Rilis Aplikasi', icon: 'download' },
  { to: '/admin/pengaturan', label: 'Pengaturan', icon: 'settings' },
]

const programName = computed(() => settings.value?.programName ?? 'Galeh Rancak')
const serverLabel = computed(() => {
  if (store.dataSourceMode === 'mock') return 'Mode demo'
  return health.value?.ok === true ? 'Layanan terhubung' : 'Layanan belum terhubung'
})

watch(() => route.fullPath, () => { menuOpen.value = false })
onMounted(() => void store.initialize())

async function logout(): Promise<void> {
  window.google?.accounts.id.disableAutoSelect()
  await store.logout()
  await router.replace({ name: 'admin-login' })
}
</script>

<template>
  <div class="admin-shell">
    <button v-if="menuOpen" class="sidebar-scrim" type="button" aria-label="Tutup menu" @click="menuOpen = false" />

    <aside class="sidebar" :class="{ 'sidebar--open': menuOpen }">
      <div class="brand">
        <img class="brand__logo-mark" src="/branding/app-icon-48.png" alt="" aria-hidden="true" />
        <div><strong>Galeh Rancak</strong><span>Panel Pengelola</span></div>
      </div>

      <nav class="sidebar__nav" aria-label="Navigasi utama">
        <RouterLink v-for="item in navigation" :key="item.to" :to="item.to" class="nav-item">
          <AppIcon :name="item.icon" :size="20" />
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>

      <div class="sidebar__privacy">
        <AppIcon name="shield" :size="19" />
        <div><strong>Privasi dijaga</strong><span>Kelola akses dan kesehatan layanan tanpa membuka isi transaksi pribadi.</span></div>
      </div>

      <div class="sidebar__status">
        <span class="status-dot" :class="{ 'status-dot--offline': health?.ok === false }" aria-hidden="true" />
        <div><strong>{{ serverLabel }}</strong><span>Versi {{ health?.version ?? '1.3.0' }}</span></div>
      </div>
    </aside>

    <section class="admin-content">
      <header class="topbar">
        <button class="icon-button mobile-only" type="button" aria-label="Buka menu" @click="menuOpen = true"><AppIcon name="menu" /></button>
        <div class="topbar__program"><strong>{{ programName }}</strong><span>Pusat kendali akun, perangkat, warung, dan layanan</span></div>
        <div class="topbar__actions">
          <StatusBadge :tone="store.dataSourceMode === 'mock' ? 'info' : health?.ok ? 'success' : 'warning'" :label="serverLabel" />
          <div class="topbar__profile"><div class="avatar" :class="{ 'avatar--image': currentUser?.avatarUrl }"><img v-if="currentUser?.avatarUrl" :src="currentUser.avatarUrl" alt="" referrerpolicy="no-referrer" /><template v-else>{{ currentUser?.displayName.charAt(0).toLocaleUpperCase('id-ID') ?? 'A' }}</template></div><div class="desktop-only"><strong>{{ currentUser?.displayName ?? 'Administrator' }}</strong><span>{{ currentUser?.email ?? 'Pengelola aplikasi' }}</span></div></div><button class="secondary-button compact-button" type="button" @click="logout">Keluar</button>
        </div>
      </header>

      <main class="page-container"><RouterView /></main>
    </section>
  </div>
</template>
